import type { ComponentType } from 'react';
import { lazy } from 'react';

const RELOAD_FLAG = 'chunk-load-reloaded';

/**
 * After a deploy the previous build's hashed chunks are gone from the server,
 * so a tab still running the old index.html fails its next lazy route import
 * ("Failed to fetch dynamically imported module"). One reload fetches the new
 * index.html and with it the new chunk URLs. The sessionStorage flag limits it
 * to a single attempt, so an import that keeps failing for another reason
 * (offline, broken build) surfaces to the route's errorElement instead of
 * reload-looping.
 */
export function lazyWithReload<T extends ComponentType>(importer: () => Promise<{ default: T }>) {
  return lazy(() =>
    importer().then(
      (module) => {
        sessionStorage.removeItem(RELOAD_FLAG);
        return module;
      },
      (error: unknown) => {
        if (sessionStorage.getItem(RELOAD_FLAG)) {
          throw error;
        }
        sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
        // The page is reloading; never resolve so nothing renders in between.
        return new Promise<{ default: T }>(() => {});
      }
    )
  );
}
